import type { SafetyFieldConfig, SafetySectionDefinition } from './types';
import { unitFieldTooltips } from '@/features/companies/components/masterDataFieldTooltips';
import { generateSafetyFormSections } from '../../utils/safetyDisplayHelpers';

/**
 * Section definitions for Abbattimento (Abatement & Discharge) sub-tab.
 */
export const abbattimentoSections: Record<string, SafetySectionDefinition> = {
  scrubber: { title: 'Scrubber', order: 1 },
  abatementSystem: { title: 'Sistema di neutralizzazione', order: 2 },
  vsRelief: { title: 'Collettori di scarico (VS)', order: 3 },
  abatementTank: { title: 'Vasca di abbattimento', order: 4 },
} as const;

/**
 * Options for abatement tank location enum.
 */
const TANK_LOCATION_OPTIONS = [
  { value: 'interrata', label: 'Interrata' },
  { value: 'fuori_terra', label: 'Fuori terra' },
];

/**
 * Options for abatement tank material enum.
 */
const TANK_MATERIAL_OPTIONS = [
  { value: 'ca', label: 'C.A.' },
  { value: 'acciaio_zincato', label: 'Acciaio zincato' },
  { value: 'vetroresina', label: 'Vetroresina' },
  { value: 'polietilene', label: 'Polietilene' },
];

/**
 * Options for abatement system control mode enum.
 */
const CONTROL_MODE_OPTIONS = [
  { value: 'manuale', label: 'Manuale' },
  { value: 'automatico', label: 'Automatico' },
];

/**
 * Field definitions for Abbattimento (Abatement & Discharge) sub-tab.
 */
export const abbattimentoFields: readonly SafetyFieldConfig[] = [
  // Scrubber section
  {
    key: 'scrubberPresent',
    dataPath: 'technical.safety.scrubber.present',
    label: 'Scrubber presente',
    type: 'checkbox',
    tooltip: unitFieldTooltips.ventilation.scrubberPresent,
    panel: 'scrubber',
    section: 'scrubber',
    displayFormatter: 'boolean',
  },
  {
    key: 'scrubberNotes',
    dataPath: 'technical.safety.scrubber.notes',
    label: 'Note',
    type: 'textarea',
    placeholder: 'Note facoltative',
    tooltip: unitFieldTooltips.ventilation.scrubberNotes,
    panel: 'scrubber',
    section: 'scrubber',
    colSpan: 2,
  },
  {
    key: 'scrubberAirFlowM3h',
    dataPath: 'technical.safety.scrubber.airFlowM3h',
    label: 'Portata aria',
    type: 'number',
    placeholder: 'Es: 10000',
    tooltip: unitFieldTooltips.ventilation.scrubberAirFlowM3h,
    panel: 'scrubber',
    section: 'scrubber',
    unit: 'm³/h',
  },
  {
    key: 'scrubberWaterFlowM3h',
    dataPath: 'technical.safety.scrubber.waterFlowM3h',
    label: 'Portata acqua',
    type: 'number',
    placeholder: 'Es: 100',
    tooltip: unitFieldTooltips.ventilation.scrubberWaterFlowM3h,
    panel: 'scrubber',
    section: 'scrubber',
    unit: 'm³/h',
  },
  {
    key: 'scrubberContactTimeSeconds',
    dataPath: 'technical.safety.scrubber.contactTimeSeconds',
    label: 'Tempo contatto',
    type: 'number',
    placeholder: 'Es: 60',
    tooltip: unitFieldTooltips.ventilation.scrubberContactTimeSeconds,
    panel: 'scrubber',
    section: 'scrubber',
    unit: 's',
  },
  {
    key: 'scrubberAtex',
    dataPath: 'technical.safety.scrubber.atex',
    label: 'Certificato ATEX',
    type: 'checkbox',
    tooltip: unitFieldTooltips.ventilation.scrubberAtex,
    panel: 'scrubber',
    section: 'scrubber',
    displayFormatter: 'boolean',
  },
  // Abatement System section
  {
    key: 'abatementSystemPresent',
    dataPath: 'technical.safety.abatementSystem.present',
    label: 'Sistema presente',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.abatementSystemPresent,
    panel: 'abatementSystem',
    section: 'abatementSystem',
    displayFormatter: 'boolean',
  },
  {
    key: 'abatementSystemControlMode',
    dataPath: 'technical.safety.abatementSystem.controlMode',
    label: 'Modalità controllo',
    type: 'select',
    options: CONTROL_MODE_OPTIONS,
    tooltip: unitFieldTooltips.safety.abatementSystemControlMode,
    panel: 'abatementSystem',
    section: 'abatementSystem',
    displayFormatter: 'select',
  },
  {
    key: 'abatementSystemTrim',
    dataPath: 'technical.safety.abatementSystem.trim',
    label: 'Trim',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.abatementSystemTrim,
    panel: 'abatementSystem',
    section: 'abatementSystem',
    displayFormatter: 'boolean',
  },
  {
    key: 'abatementSystemLimitSwitches',
    dataPath: 'technical.safety.abatementSystem.limitSwitches',
    label: 'Finecorsa',
    type: 'checkbox',
    tooltip: unitFieldTooltips.safety.abatementSystemLimitSwitches,
    panel: 'abatementSystem',
    section: 'abatementSystem',
    displayFormatter: 'boolean',
  },
  {
    key: 'abatementSystemWaterFlowM3h',
    dataPath: 'technical.safety.abatementSystem.waterFlowM3h',
    label: 'Portata acqua',
    type: 'number',
    placeholder: 'Es: 50',
    tooltip: unitFieldTooltips.safety.abatementSystemWaterFlowM3h,
    panel: 'abatementSystem',
    section: 'abatementSystem',
    unit: 'm³/h',
  },
  // VS Relief section
  {
    key: 'vsReliefCollectorsCount',
    dataPath: 'technical.safety.vsRelief.collectorsCount',
    label: 'Collettori (n°)',
    type: 'number',
    placeholder: 'Es: 2',
    tooltip: unitFieldTooltips.discharge.vsReliefCollectorsCount,
    panel: 'vsRelief',
    section: 'vsRelief',
  },
  {
    key: 'vsReliefSizing',
    dataPath: 'technical.safety.vsRelief.sizing',
    label: 'Dimensionamento',
    type: 'text',
    placeholder: 'Es: UNI EN 13136',
    tooltip: unitFieldTooltips.discharge.vsReliefSizing,
    panel: 'vsRelief',
    section: 'vsRelief',
  },
  // Abatement Tank section
  {
    key: 'abatementTankPresent',
    dataPath: 'technical.safety.abatementTank.present',
    label: 'Vasca presente',
    type: 'checkbox',
    panel: 'abatementTank',
    section: 'abatementTank',
    displayFormatter: 'boolean',
  },
  {
    key: 'abatementTankVolumeMc',
    dataPath: 'technical.safety.abatementTank.volumeMc',
    label: 'Volume vasca',
    type: 'number',
    placeholder: 'Es: 50',
    tooltip: unitFieldTooltips.discharge.abatementVolumeMc,
    panel: 'abatementTank',
    section: 'abatementTank',
    unit: 'm³',
  },
  {
    key: 'abatementTankMinWaterMc',
    dataPath: 'technical.safety.abatementTank.minWaterMc',
    label: 'Acqua minima',
    type: 'number',
    placeholder: 'Es: 30',
    tooltip: unitFieldTooltips.discharge.abatementMinWaterMc,
    panel: 'abatementTank',
    section: 'abatementTank',
    unit: 'm³',
  },
  {
    key: 'abatementTankLocation',
    dataPath: 'technical.safety.abatementTank.location',
    label: 'Ubicazione',
    type: 'select',
    options: TANK_LOCATION_OPTIONS,
    tooltip: unitFieldTooltips.discharge.abatementLocation,
    panel: 'abatementTank',
    section: 'abatementTank',
    displayFormatter: 'select',
  },
  {
    key: 'abatementTankMaterial',
    dataPath: 'technical.safety.abatementTank.material',
    label: 'Materiale',
    type: 'select',
    options: TANK_MATERIAL_OPTIONS,
    tooltip: unitFieldTooltips.discharge.abatementMaterial,
    panel: 'abatementTank',
    section: 'abatementTank',
    displayFormatter: 'select',
  },
  {
    key: 'abatementTankLevelInstrumentationPresent',
    dataPath: 'technical.safety.abatementTank.levelInstrumentation.present',
    label: 'Strumentazione livello',
    type: 'checkbox',
    tooltip: unitFieldTooltips.discharge.abatementLevelInstrumentationPresent,
    panel: 'abatementTank',
    section: 'abatementTank',
    displayFormatter: 'boolean',
  },
  {
    key: 'abatementTankContinuousPhMonitoring',
    dataPath: 'technical.safety.abatementTank.continuousPhMonitoring',
    label: 'Monitoraggio pH',
    type: 'checkbox',
    tooltip: unitFieldTooltips.discharge.abatementContinuousPhMonitoring,
    panel: 'abatementTank',
    section: 'abatementTank',
    displayFormatter: 'boolean',
  },
] as const;

/**
 * Form sections derived from field definitions.
 */
export const abbattimentoFormSections = generateSafetyFormSections(
  abbattimentoFields,
  ['scrubber', 'abatementSystem', 'vsRelief', 'abatementTank'],
  {
    scrubber: 'Scrubber',
    abatementSystem: 'Sistema di neutralizzazione',
    vsRelief: 'Collettori di scarico (VS)',
    abatementTank: 'Vasca di abbattimento',
  }
);
